/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { Formik } from 'formik';
import _ from 'lodash';
import { isEmptyAddress } from 'modules/core/helpers/is-empty-address';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useEffect, useState } from 'react';
import { t as coreT } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { toast } from 'react-toastify';
import { SUPPORT_URL } from 'shared/constants/links';
import { usePreventNavigation } from 'shared/hooks';
import { useImageUpscaleFieldOption } from 'shared/hooks/use-image-upscale-access';
import { UDFormDropzone } from 'ud-form/components/image-dropzone';
import UDFormInput from 'ud-form/components/input';
import UDFormTextarea from 'ud-form/components/input/textarea';
import { UDFormAddressSelect } from 'ud-form/components/select';
import UDButton from 'ud-ui/components/button';

import { DadataAreaType } from 'ud-ui/components/dadata-address-autocomplete/domain/entities/dadata-area.entity';
import {
  mapAddressToDadataArea,
  mapDadataAreaToAddress,
} from 'ud-ui/components/dadata-address-autocomplete/infra/data-mappers/dadata-area.data-mapper';
import { useImageGallery } from 'ud-ui/components/image-gallery/modal';
import UDText from 'ud-ui/components/text';
import * as yup from 'yup';
import { UpsertLocationInput } from 'modules/locations/domain/interfaces/actions';

import { Image, Location } from 'modules/locations/domain/interfaces/location';
import { LocationFormContainer, LocationMapImage, LocationMapImageContainer } from './styles';

type LocationFormProps = {
  location?: Location;
  isLoading: boolean;
  onSubmit: (values: UpsertLocationInput) => Promise<void>;
  tournamentId?: number;
  action: 'create' | 'edit';
  hasUnsavedChanges?: boolean;
  isAvailableFeatures?: boolean;
  isMapImageLoading?: boolean;
};

// Сообщения — функции: yup зовёт их в момент валидации, с текущим языком.
const locationValidator = yup.object().shape({
  name: yup.string().required(() => coreT`Название обязательно`),
  address: yup
    .object()
    .nullable()
    .required(() => coreT`Адрес обязателен`),
  images: yup
    .array()
    .max(5, () => coreT`Максимум 5 фотографий`)
    .test(
      'unique',
      () => coreT`Изображения должны быть уникальными`,
      function (value) {
        if (!value) return true;

        const files = value.reduce<File[]>((acc, img: File | Image) => {
          if (img instanceof File) return [...acc, img];
          return acc;
        }, []);

        return files.length === new Set(files.map((file: File) => file.name)).size;
      },
    ),
});

function LocationForm({
  location,
  isLoading,
  onSubmit,
  tournamentId,
  action,
  hasUnsavedChanges,
  isAvailableFeatures,
  isMapImageLoading,
}: LocationFormProps) {
  const [descriptionHeight, setDescriptionHeight] = useState<string>('flex-grow: 1');
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useLingui();
  const photoUpscale = useImageUpscaleFieldOption('photo');
  const { GalleryModalWrapper, handleImageClick, setImages } = useImageGallery({
    size: { width: '650px', height: '450px' },
  }); // Инициализация галереи

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { backHandler, PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.LOCATIONS.INDEX,
  });

  const openMapImageGallery = useCallback(() => {
    setImages([
      {
        url: `data:image/png;base64,${location?.mapImage}`,
        externalUrl: `data:image/png;base64,${location?.mapImage}`,
      },
    ]);
    handleImageClick(0);
  }, [setImages, location?.mapImage, handleImageClick]);

  useEffect(() => {
    const description = document.getElementById('description');
    if (!description) {
      return;
    }

    const observer = new MutationObserver(() => {
      setDescriptionHeight(description.style.height);
    });

    observer.observe(description, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const initialValues = location
    ? {
        name: location.name,
        address: location.address && !isEmptyAddress(location.address) ? mapAddressToDadataArea(location.address) : null,
        description: location.description || '',
        images: location.images || [],
      }
    : {
        name: '',
        address: null,
        description: '',
        images: [],
      };

  const handleSubmit = useCallback(
    (values: typeof initialValues) => {
      const effectiveTournamentId = location?.tournamentId || tournamentId;

      if (!effectiveTournamentId) {
        toast.error(t`ID организатора не найден, попробуйте обновить страницу`);
        throw new Error('Tournament ID is required for location');
      }

      const locationData: UpsertLocationInput = {
        id: location?.id,
        name: values.name,
        description: values.description,
        images: values.images as Array<File | Image>,
        tournamentId: effectiveTournamentId,
        address: values.address ? mapDadataAreaToAddress(values.address) : undefined,
      };
      setIsProcessing(true);
      onSubmit(locationData).finally(() => {
        setIsProcessing(false);
      });
    },
    [location?.id, location?.tournamentId, tournamentId, onSubmit, t],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={locationValidator}
      onSubmit={(values, formikHelpers) => {
        handleSubmit(values);
        formikHelpers.resetForm({ values }); // делаем текущие значения базой
        setIsFormDirty(false);
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {(formProps) => {
        const isDisabled = Boolean(
          isLoading ||
            isProcessing ||
            formProps.errors?.name ||
            formProps.errors?.address ||
            formProps.errors?.description ||
            formProps.errors?.images,
        );

        return (
          <form className="flex h-full flex-col justify-between" onSubmit={formProps.handleSubmit}>
            <LocationFormContainer>
              <div className="flex gap-5">
                <div className="flex w-1/2 flex-col">
                  <UDFormInput name="name" label={t`Название*`} placeholder={t`Введите название места проведения`} required={true} />

                  <div className="location-data flex flex-col">
                    <UDFormAddressSelect
                      name="address"
                      required={true}
                      label={t`Город и адрес*`}
                      placeholder={t`Введите адрес`}
                      onlyCity={false}
                      allowedTypes={[DadataAreaType.REGION, DadataAreaType.CITY, DadataAreaType.STREET, DadataAreaType.HOUSE]}
                    />
                    {(location?.mapImage || isMapImageLoading) && (
                      <LocationMapImageContainer onClick={openMapImageGallery}>
                        {isMapImageLoading && <div className="skeleton-block" style={{ width: '100%', height: 425 }} />}
                        {location?.mapImage && <LocationMapImage src={`data:image/png;base64,${location.mapImage}`} alt={t`Карта`} />}
                      </LocationMapImageContainer>
                    )}
                  </div>

                  <div
                    className="location-data"
                    css={css`
                      ${descriptionHeight}
                    `}
                  >
                    <UDFormTextarea
                      name="description"
                      label={t`Описание`}
                      required={false}
                      placeholder={t`Опишите локацию как можно подробнее, расскажите о преимуществах`}
                      style={{ height: '100%' }}
                      id="description"
                      containerProps={{ className: 'flex flex-col w-full' }}
                      wrapperProps={{ style: { flexGrow: 1 } }}
                    />
                  </div>
                </div>

                <div className="w-1/2">
                  <UDFormDropzone
                    name="images"
                    label={t`Загрузите фото локации (до 5 шт.)`}
                    multiple
                    maxFiles={5}
                    sortable
                    removable
                    upscale={photoUpscale}
                    className="p-6"
                    disabled={!isAvailableFeatures}
                    title={
                      isAvailableFeatures
                        ? t`Загрузить фото локации`
                        : t`Загрузка фото доступна только пользователям\nс платной подпиской.`
                    }
                    subtitle={isAvailableFeatures ? t`с вашего устройства` : ''}
                    buttonText={isAvailableFeatures ? t`Выбрать файлы` : t`Подключить подписку`}
                    onButtonClick={() => {
                      if (!isAvailableFeatures) {
                        window.open(SUPPORT_URL, '_blank');
                      }
                    }}
                  />
                  {!isAvailableFeatures && (
                    <UDText type="caption-1" className="text-surfaceRomanSilver30 mt-2">
                      <Trans>Для редактирования фотографий необходимо иметь активную подписку.</Trans>
                    </UDText>
                  )}
                </div>
              </div>

              <div className="mb-6 flex justify-between" style={{ width: '50%', paddingRight: 24, marginTop: 60 }}>
                <UDButton
                  type="submit"
                  variant="primary"
                  disabled={hasUnsavedChanges === undefined ? isDisabled : isDisabled || !hasUnsavedChanges}
                  loading={isProcessing}
                  className="mr-4"
                  style={{ width: '50%' }}
                >
                  {action === 'create' ? t`Создать` : t`Сохранить`}
                </UDButton>

                <UDButton
                  type="button"
                  variant="secondary"
                  disabled={isLoading || isProcessing}
                  onClick={backHandler}
                  style={{ width: '50%' }}
                >
                  {t`Отменить`}
                </UDButton>
              </div>
              <GalleryModalWrapper />
              <PreventNavigationModal />
            </LocationFormContainer>
          </form>
        );
      }}
    </Formik>
  );
}

export default LocationForm;
